import React, { useState } from 'react';
import {
  SidePanelSearchClinicPetParentFragment,
  useGetSidePanelPetProfileLazyQuery,
  useUpdateOneClinicPetParentMutation,
} from 'shared/types/graphql';
import PetParentAvatar from 'shared/components/Avatars/PetParentAvatar';
import { useSidePanelSearch } from '../../../state/providers/SearchPanelProvider';
import { formatPluralization, getClinicPetParentDisplayName } from 'shared/utils';
import { Box, Center, HStack, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import sortBy from 'lodash-es/sortBy';
import LoadingSpinner from '../../../../../../LoadingSpinner';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { Mode } from '../../types/enum/Mode';
import { GraphQLFetchPolicies } from 'shared/enums';
import { Modal, ModalBody, ModalCloseButton, ModalFooterButtons, ModalHeader } from '@televet/kibble-ui';
import useClinicUser from 'shared/hooks/useClinicUser';

interface IPetParentListProps {
  petParentList: SidePanelSearchClinicPetParentFragment[];
  isGettingClinicPetParentsData: boolean;
}

const PetParentList = ({ petParentList, isGettingClinicPetParentsData }: IPetParentListProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { viewClinicPetParent, searchPanelMode, selectedPatient, viewClinicPet, setSearchPanelMode } =
    useSidePanelSearch();
  const { isClientIdSupported } = useIntegrationsProvider();

  const [updateClinicPetParent] = useUpdateOneClinicPetParentMutation();
  const [getSidePanelPetProfile] = useGetSidePanelPetProfileLazyQuery();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPetParent, setSelectedPetParent] = useState<SidePanelSearchClinicPetParentFragment>();
  return (
    <Box className="PetParentResults__List" mt={6} mb={8}>
      <Text className="PetParentResults_Header" fontWeight="bold" px={4} pb={2}>
        {searchPanelMode === Mode.LinkToPet ? `Select a client for ${selectedPatient?.name}:` : 'Clients'}
      </Text>
      {isGettingClinicPetParentsData && (!petParentList || petParentList.length === 0) && (
        <LoadingSpinner size="md" message="Loading Clients..." />
      )}
      {(!petParentList || petParentList.length === 0) && !isGettingClinicPetParentsData && (
        <Center color="text.subtle" py="3" fontSize="14">
          No results found
        </Center>
      )}
      {searchPanelMode === Mode.LinkToPet && selectedPetParent && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalCloseButton />
          <ModalHeader>Add patient to client</ModalHeader>
          <ModalBody>
            Are you sure you want to add {selectedPatient?.name} to {selectedPetParent?.firstName}{' '}
            {selectedPetParent?.lastName}
          </ModalBody>

          <ModalFooterButtons
            onCancel={onClose}
            onSubmit={async (): Promise<void> => {
              if (!selectedPatient) return;
              await updateClinicPetParent({
                variables: {
                  where: { id: selectedPetParent.id },
                  data: { pets: { connect: [{ id: selectedPatient.id }] } },
                },
              });
              await getSidePanelPetProfile({
                variables: { clinicPetId: selectedPatient.id, clinicId: currentClinicId || '' },
                fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
              });
              setSearchPanelMode(Mode.Default);
              viewClinicPet({ clinicPetId: selectedPatient.id });
            }}
          />
        </Modal>
      )}
      {!isGettingClinicPetParentsData &&
        petParentList &&
        petParentList.length > 0 &&
        sortBy(
          petParentList,
          (petParent) => `${petParent?.firstName.toLowerCase()} ${petParent?.lastName.toLowerCase()}`,
        ).map((parent: SidePanelSearchClinicPetParentFragment, index) => {
          const { firstName, lastName, pets, id, pimsId, creationSource, phoneNumbers } = parent;
          const petNames = pets?.map((pet) => pet.name);
          return (
            <HStack
              className="PetParentResults__Row"
              data-testid={`pet-parent-list-item-${index}`}
              key={id}
              py={3}
              px={4}
              spacing={3}
              justify="flex-start"
              onClick={(e): void => {
                e.stopPropagation();
                if (searchPanelMode === Mode.LinkToPet && selectedPatient) {
                  onOpen();
                  setSelectedPetParent(parent);
                } else {
                  viewClinicPetParent({ clinicPetParentId: id });
                }
              }}
              cursor="pointer"
              _hover={{
                bg: 'background.alpha.hover',
              }}
            >
              <Box className="PetParentResults__PetParentAvatar" alignSelf="center">
                <PetParentAvatar clinicPetParent={parent} size="lg" />
              </Box>

              <Box className="PetParentResults__PetParentDetails" alignSelf="flex-start">
                <Text as="p" pt={1} fontWeight="bold" data-testid={`pet-parent-name-${index}`}>
                  {getClinicPetParentDisplayName({ firstName, lastName, creationSource, phoneNumbers })}
                  {isClientIdSupported && pimsId && <Text size="xs">{` - ID: ${pimsId}`}</Text>}
                </Text>
                <Text noOfLines={2} variant="subtle" size="sm" data-testid={`pet-name-${index}`} isTruncated>
                  {pets?.length > 0
                    ? `${formatPluralization({ word: 'Patient', singularCriteria: pets?.length })}: ${petNames?.join(
                        ', ',
                      )}`
                    : 'No Patients'}
                </Text>
              </Box>
            </HStack>
          );
        })}
    </Box>
  );
};

export default PetParentList;
